import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "dx-literacy",
  name: "DXリテラシー",
  nameEn: "DX Literacy",
  seo: {
    title:
      "DXリテラシーを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "デジタルトランスフォーメーションの基礎から業務改善・ビジネスとITの接続まで、DXリテラシーをゆめさくエンジニアで実践的に学びましょう。DX推進人材を目指す方に最適です。",
    url: "/columns/dx-literacy-programming-school",
  },
  hero: {
    title: "DXリテラシーを学べる女性向けプログラミングスクール",
    englishTitle: "Learn DX Literacy",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "DXリテラシー" },
  ],
  overview: {
    heading: "DXリテラシーとは？",
    paragraphs: [
      "DX（デジタルトランスフォーメーション）リテラシーとは、デジタル技術を活用してビジネスや業務を変革するための基礎的な知識・スキルのことです。経済産業省も「DXリテラシー標準」を策定し、すべてのビジネスパーソンに求められる能力として位置づけています。",
      "DXリテラシーはプログラミングスキルだけではなく、デジタル技術の概要理解、データ活用の基礎、業務プロセスの改善手法、ITとビジネスを橋渡しするコミュニケーション力など、幅広い知識を含みます。",
      "IT部門だけでなく、営業・マーケティング・人事・経理など、すべての部門でDXリテラシーを持つ人材が求められており、キャリアの幅を広げる強力な武器となります。",
    ],
    useCases: [
      {
        label: "業務プロセス改善",
        description:
          "手作業で行っている業務を分析し、RPA・ノーコードツール・クラウドサービスを活用して効率化・自動化を推進します。",
      },
      {
        label: "データ活用・意思決定支援",
        description:
          "社内データを収集・分析し、経営判断やマーケティング施策にデータドリブンなアプローチを導入します。",
      },
      {
        label: "IT企画・要件定義",
        description:
          "ビジネス部門のニーズをITの言葉に翻訳し、システム導入やサービス開発の企画・要件定義を行います。",
      },
      {
        label: "社内DX推進",
        description:
          "社内のデジタル化を推進するリーダーとして、ツール選定・導入計画・社員教育などを主導します。",
      },
    ],
  },
  whyLearn: {
    heading: "DXリテラシーを学ぶべき3つの理由",
    items: [
      {
        title: "あらゆる業界・職種で求められるスキル",
        description:
          "DXはIT企業だけでなく、製造・小売・金融・医療・行政など全業界で推進されています。DXリテラシーを持つ人材は業界を問わず需要が高く、キャリアの選択肢が大幅に広がります。",
      },
      {
        title: "「ビジネス×IT」の橋渡し人材は希少",
        description:
          "技術だけ分かるエンジニアやビジネスだけ分かる営業職は多くいますが、両方を理解し橋渡しできる人材は非常に少なく、高い市場価値を持ちます。DXリテラシーはその架け橋となるスキルです。",
      },
      {
        title: "非エンジニアからのキャリアチェンジに最適",
        description:
          "DXリテラシーはプログラミング未経験からでも学び始められ、業務経験を活かしながらIT分野へのキャリアチェンジを実現できます。ビジネス経験がむしろ強みとなる領域です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでDXリテラシーを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアでは、オンボーディング期間でDXリテラシーの基礎を固めます。デジタル技術の全体像を理解し、データ活用・業務改善の基本的な考え方を身につけた上で、プログラミング学習に進みます。",
      "ビジネス経験を持つ方の強みを活かし、IT知識と業務改善スキルを組み合わせて「DX推進人材」として活躍できるカリキュラムを提供しています。",
    ],
    topics: [
      "DXの全体像と企業の変革事例",
      "クラウド・AI・IoTなどデジタル技術の基礎理解",
      "データリテラシー（データの収集・分析・活用）",
      "業務プロセス分析と改善手法",
      "ノーコード・ローコードツールの活用",
      "ITプロジェクトの基礎（要件定義・進行管理）",
      "情報セキュリティ・個人情報保護の基礎",
    ],
  },
  careerPaths: {
    heading: "DXリテラシーを活かせるキャリア",
    items: [
      {
        title: "DX推進担当",
        description:
          "社内のデジタル化・業務改善を推進するポジションです。業務部門とIT部門の橋渡し役として、ツール選定・導入計画・効果測定などを担当します。",
        salary: "400〜700万円",
      },
      {
        title: "ITコンサルタント",
        description:
          "クライアント企業のDX戦略立案や、システム導入支援を行う職種です。ビジネス理解とIT知識の両方が求められ、DXリテラシーが直接活かせます。",
        salary: "500〜900万円",
      },
      {
        title: "社内SE",
        description:
          "自社の情報システム部門で、業務システムの企画・運用・改善を担当する職種です。社内の業務プロセスを理解し、ITで効率化を図ります。",
        salary: "400〜650万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "3〜6ヶ月（情報が広範で何を学ぶべきか迷いやすい）",
        luagate: "1〜2ヶ月（実務に必要な知識を厳選して効率的に習得）",
      },
      {
        label: "挫折率",
        selfStudy: "範囲が広すぎて何から始めれば良いかわからず挫折",
        luagate: "体系的なカリキュラムで迷わず学習を進められる",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "資格勉強で終わり、実務で使えない",
        luagate: "実際の業務改善ケーススタディで実践力を養成",
      },
      {
        label: "質問対応",
        selfStudy: "ITの専門用語やツール選定で行き詰まりやすい",
        luagate: "メンターがビジネス文脈に沿って丁寧にサポート",
      },
      {
        label: "就職サポート",
        selfStudy: "DX関連の求人をどう探せば良いかわからない",
        luagate: "DX推進ポジションへのキャリアパスを具体的にサポート",
      },
    ],
  },
  faq: [
    {
      question: "DXリテラシーとプログラミングスキルの違いは何ですか？",
      answer:
        "プログラミングは「技術を使って作る力」、DXリテラシーは「デジタル技術をビジネスに活かす力」です。DXリテラシーにはプログラミング以外にも、データ活用・業務改善・プロジェクト管理・セキュリティなど幅広い知識が含まれます。",
    },
    {
      question: "IT未経験の事務職ですが、DXリテラシーを学ぶ意味はありますか？",
      answer:
        "大いにあります。むしろ業務の現場を知っている方こそ、DXリテラシーを身につけることで「業務の課題をITで解決する」提案ができるようになります。現場経験はDX推進において大きな強みです。",
    },
    {
      question: "DXリテラシーだけで転職できますか？",
      answer:
        "DXリテラシーに加えてプログラミングの基礎やデータ分析スキルがあると、転職の選択肢が大幅に広がります。ゆめさくエンジニアではDXリテラシーを土台として、プログラミングスキルも段階的に習得できるカリキュラムを提供しています。",
    },
    {
      question: "DX関連の資格は取得できますか？",
      answer:
        "ゆめさくエンジニアのカリキュラムはITパスポートやDXリテラシー標準の内容をカバーしているため、資格取得の基礎力も自然と身につきます。ただし資格対策に特化したコースではなく、実務で使えるスキルの習得を重視しています。",
    },
    {
      question: "DXリテラシーの学習にPCスキルはどの程度必要ですか？",
      answer:
        "基本的なPC操作（メール、Webブラウジング、Excel程度）ができれば問題ありません。ゆめさくエンジニアではクラウドツールの使い方から丁寧に指導しますので、PCに自信がない方でも安心して学べます。",
    },
  ],
  relatedLanguages: [
    { slug: "nocode-ai", name: "ノーコードAI" },
    { slug: "generative-ai", name: "生成AI" },
    { slug: "prompt-engineering", name: "プロンプトエンジニアリング" },
    { slug: "cs-basics", name: "コンピュータサイエンス基礎" },
  ],
};
